import React from 'react'
import { Button, Card, Col, Row } from 'react-bootstrap'
import { ArrowUp, ArrowDown, CheckLg } from 'react-bootstrap-icons'
import {
  useAppDispatch,
  useAppSelector,
  useSelectTagsByDataset,
  useAnnotationStats,
} from '../store/hooks'
import '../styles/_mini-class-distribution-card.scss'
import {
  selectFilterTags,
  toggleFilterTag,
  selectActiveTab,
  selectSelectedProjectId,
} from '../store/slices/projectSlice'
import { Tag } from '../types/models/tag'
import { selectDatasetSources } from '../store/slices/datasetSlice'
import { selectSelectedModelId } from '../store/slices/modelSlice'
import { useGetModelQuery } from '../services/modelsApi'
import { useGetProjectQuery } from '../services/projectsApi'
import { log } from 'console'

interface MiniClassDistributionCardProps {
  title?: string
  showButtons?: boolean
  datasetId?: string
}

const getBalanceIcon = (balance: string) => {
  if (balance.includes('balanced')) {
    return <CheckLg size={16} color={'green'} />
  } else if (balance.includes('under')) {
    return <ArrowDown size={16} color={'red'} />
  } else if (balance.includes('over')) {
    return <ArrowUp size={16} color={'orange'} />
  }
}

const MiniClassDistributionCard: React.FC<MiniClassDistributionCardProps> = ({
  title = 'Class Distribution',
  showButtons = true,
  datasetId,
}) => {
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const activeTab = useAppSelector(selectActiveTab)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const { data: selectedModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })

  const datasetSources = useAppSelector(selectDatasetSources)
  const modelSources = Array.from(
    new Set([
      ...(selectedModel?.parameters?.train_metadata?.datasource || []),
      ...(selectedModel?.parameters?.validation_metadata?.datasource || []),
    ])
  )

  const stats = useAnnotationStats(
    selectedProjectId || '',
    datasetId || project?.datasetid || '',
    modelSources?.length > 0
      ? modelSources
      : datasetSources?.length > 0
      ? datasetSources
      : undefined
  )

  const tags = useSelectTagsByDataset(
    selectedProjectId || '',
    datasetId || project?.datasetid || ''
  )
  const filterTags = useAppSelector(selectFilterTags)

  const handleToggleFilterTag = (tagName: string) => {
    if (activeTab === 'content') return

    if (tagName === 'Unlabeled') {
      // Create a special tag for unlabeled filtering
      const unlabeledTag: Tag = {
        id: 'unlabeled',
        name: 'Unlabeled',
        color: parseInt('808080', 16),
        property: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        datasetid: datasetId || project?.datasetid || '',
        projectid: selectedProjectId || '',
        userid: '',
      }
      dispatch(toggleFilterTag(unlabeledTag))
    } else {
      const selectedTag = tags?.find((t) => t.name === tagName)
      if (selectedTag) {
        dispatch(toggleFilterTag(selectedTag))
      }
    }
  }

  const isTagSelected = (tagName: string): boolean => {
    return filterTags.some((tag: Tag) => tag.name === tagName)
  }

  // Create class distribution from annotated statistics
  const miniClassDistribution = React.useMemo(() => {
    if (!stats?.data?.statistics?.annotations_per_class || !tags) return []

    // Map all tags to ensure we include even those without stats
    const dist = tags
      .map((tag) => {
        const classStats = stats?.data?.statistics?.annotations_per_class[tag.id]
        return {
          id: tag.id,
          name: tag.name,
          value: classStats?.count || 0,
          color: `#${tag.color.toString(16).padStart(6, '0').slice(0, 6)}`,
          balance: classStats?.balance || 'representation_balanced',
        }
      })
      .sort((a, b) => b.value - a.value)

    console.log(dist)
    return dist
  }, [stats, tags])

  return (
    <Card className="mini-class-distribution-card mt-3 shadow-sm">
      <Card.Title>Class Distribution</Card.Title>
      <Card.Body>
        {showButtons && (
          <Row className="title-row align-items-center">
            <Col>
              <Card.Title className="card-title mb-0">{title}</Card.Title>
            </Col>
            <Col xs="auto">
              <Button size="sm" variant="primary">
                Add Data
              </Button>
            </Col>
          </Row>
        )}
        <hr className="divider" />
        {miniClassDistribution.map((item) => (
          <React.Fragment key={item.id}>
            <Row
              className={`class-item-row align-items-center mb-1 ${
                isTagSelected(item.name) ? 'selected' : ''
              }`}
            >
              <Col xs={2} className="color-circle-col">
                <div className="color-circle" style={{ backgroundColor: item.color }}></div>
              </Col>
              <Col
                xs={6}
                className={`class-name ${activeTab === 'content' ? '' : 'clickable'}`}
                onClick={
                  activeTab === 'content' ? undefined : () => handleToggleFilterTag(item.name)
                }
                style={{
                  cursor: activeTab === 'content' ? 'default' : 'pointer',
                }}
              >
                {item.name}
              </Col>
              <Col xs={3} className="value">
                {item.value}
              </Col>
              <Col xs={1} className="balance-icon">
                {getBalanceIcon(item.balance)}
              </Col>
            </Row>
          </React.Fragment>
        ))}
        <hr className="divider" />
        <Row
          className={`class-item-row align-items-center mb-1 ${
            isTagSelected('Unlabeled') ? 'selected' : ''
          }`}
        >
          <Col xs={2} className="color-circle-col">
            <div className="color-circle" style={{ backgroundColor: '#808080' }}></div>
          </Col>
          <Col
            xs={6}
            className={`class-name ${activeTab === 'content' ? '' : 'clickable'}`}
            onClick={activeTab === 'content' ? undefined : () => handleToggleFilterTag('Unlabeled')}
            style={{ cursor: activeTab === 'content' ? 'default' : 'pointer' }}
          >
            Unlabeled
          </Col>
          <Col xs={3} className="value">
            {/* No count shown for unlabeled */}
          </Col>
          <Col xs={1} className="balance-icon">
            {/* No balance icon for unlabeled */}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}

export default MiniClassDistributionCard
